/**
 * SINGLE SOURCE OF TRUTH for turning job form state into an API payload.
 *
 * The form gives every row a client-side `id` from crypto.randomUUID() so that
 * React keys and drag-and-drop have something stable to hold onto. Those ids
 * mean nothing to the backend and must not be persisted - on the next load the
 * rows get fresh ones, so a stored temp id can never be matched again.
 *
 * equipment_display_order is the interesting part. It is a list of ordering
 * hints, and each entry has to be rewritten in terms of something that
 * survives a round trip:
 *
 *   equipment        the inventory_id it points at
 *   package          its sorted equipment_ids, joined - packages have no
 *                    stable id of their own, and names can repeat
 *   divider          its own id, which is the only thing identifying it
 *   crew_equipment   its own id, likewise
 *
 * JobBuilderPage and JobEditPage each wrote their own version of this and
 * disagreed on the edges - see the notes on the fallbacks below.
 */

/** Strip the client-side `id` from every row of a collection. */
const withoutIds = (rows) => (rows || []).map(({ id, ...rest }) => rest);

/**
 * Rewrite one display-order entry so it survives being saved and reloaded.
 *
 * Where an entry cannot be resolved, it is returned untouched rather than
 * dropped. Dropping it would remove that item from the ordered list the job
 * pages render from, which can hide equipment. The edit page used to write the
 * temp id into `inventory_id`, which is worse than leaving it alone: it claims
 * to be an inventory reference and is not.
 */
function persistableOrderItem(orderItem, formData) {
  if (orderItem.type === 'equipment') {
    const eq = (formData.equipment || []).find(e => e.id === orderItem.id);
    return eq ? { type: 'equipment', inventory_id: eq.inventory_id } : orderItem;
  }

  if (orderItem.type === 'package') {
    const pkg = (formData.equipment_packages || []).find(p => p.id === orderItem.id);
    if (pkg?.equipment_ids?.length > 0) {
      return {
        type: 'package',
        equipment_ids_key: [...pkg.equipment_ids].sort().join(','),
        name: pkg.name,
      };
    }
    return orderItem;
  }

  if (orderItem.type === 'divider') {
    return { type: 'divider', divider_id: orderItem.id, label: orderItem.label || '' };
  }

  if (orderItem.type === 'crew_equipment') {
    return { type: 'crew_equipment', crew_equipment_id: orderItem.id };
  }

  // An unrecognised type is passed through untouched. The edit page used to
  // treat anything that was not a package, divider or crew equipment as
  // equipment, which mislabelled it.
  return orderItem;
}

/**
 * Build the request body for creating or updating a job.
 *
 * The same payload serves both: create POSTs it, edit PUTs it. Nothing about
 * the shape differs between the two.
 *
 * @param {object} formData - the job form's state
 * @returns {object} the payload to send to the API
 */
export function buildJobPayload(formData) {
  const f = formData || {};
  return {
    ...f,
    phases: withoutIds(f.phases),
    crew_labour: withoutIds(f.crew_labour),
    // Crew equipment keeps its id: display order refers to it by that id.
    crew_equipment: f.crew_equipment,
    equipment: withoutIds(f.equipment),
    equipment_packages: withoutIds(f.equipment_packages),
    expenses: withoutIds(f.expenses),
    hidden_costs: withoutIds(f.hidden_costs),
    equipment_display_order: (f.equipment_display_order || [])
      .map(orderItem => persistableOrderItem(orderItem, f)),
  };
}
